import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { User, UserRoleLabels } from '../../types';
import { Avatar } from './Avatar';

interface MemberSearchSelectProps {
  /** All users available for selection */
  users: User[];
  /** Currently selected user IDs */
  selectedIds: string[];
  /** Called when a user is toggled on/off */
  onToggle: (userId: string) => void;
  /** Allow multi-select (checkboxes) or single-select (click to pick) */
  multiSelect?: boolean;
  /** Placeholder shown on the trigger button */
  placeholder?: string;
  /** Optional: only show users with specific roles */
  filterRoles?: string[];
  /** CSS class for wrapping div */
  className?: string;
}

/**
 * Dropdown button for selecting project members.
 * Opens a portal dropdown with a search bar and checkbox list.
 * No chips — just toggle directly from the dropdown.
 */
export const MemberSearchSelect: React.FC<MemberSearchSelectProps> = ({
  users,
  selectedIds,
  onToggle,
  multiSelect = true,
  placeholder = 'Select members...',
  filterRoles,
  className = '',
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 300 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter users by search term and optional role filter
  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (filterRoles && filterRoles.length > 0) {
      filtered = filtered.filter(u => filterRoles.includes(u.role));
    }
    if (!search.trim()) return filtered;
    const term = search.toLowerCase();
    return filtered.filter(
      u =>
        u.name.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        (UserRoleLabels[u.role] || u.role).toLowerCase().includes(term)
    );
  }, [users, search, filterRoles]);

  // Position dropdown portal
  const updatePos = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const dropdownHeight = 300;
    const ddWidth = Math.max(280, rect.width);
    const spaceBelow = window.innerHeight - rect.bottom;

    // Clamp left so the dropdown doesn't overflow the right edge
    const maxLeft = window.innerWidth - ddWidth - 8;
    const clampedLeft = Math.max(4, Math.min(rect.left, maxLeft));

    if (spaceBelow >= dropdownHeight || spaceBelow > rect.top) {
      setDropdownPos({ top: rect.bottom + 2, left: clampedLeft, width: ddWidth });
    } else {
      setDropdownPos({ top: rect.top - Math.min(dropdownHeight, rect.top), left: clampedLeft, width: ddWidth });
    }
  }, []);

  // Close on outside click/touch
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Reposition on resize/scroll instead of closing (mobile keyboard, orientation, etc.)
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updatePos();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updatePos]);

  useEffect(() => {
    if (isOpen) {
      updatePos();
      // Only auto-focus search on non-touch devices to avoid triggering virtual keyboard
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (!isTouchDevice) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  }, [isOpen, updatePos]);

  const selectedCount = selectedIds.length;

  // Build trigger label
  const triggerLabel = useMemo(() => {
    if (selectedCount === 0) return placeholder;
    const selectedNames = users
      .filter(u => selectedIds.includes(u.id))
      .map(u => u.name.split(' ')[0]);
    if (selectedNames.length <= 2) return selectedNames.join(', ');
    return `${selectedNames.slice(0, 2).join(', ')} +${selectedNames.length - 2}`;
  }, [users, selectedIds, selectedCount, placeholder]);

  const handleToggleOpen = () => {
    setIsOpen(prev => !prev);
    if (isOpen) setSearch('');
  };

  const dropdown = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="member-dd-portal"
      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
    >
      {/* Search inside dropdown */}
      <div className="member-dd-search-box">
        <input
          ref={searchInputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search..."
          className="member-dd-search-input"
        />
      </div>

      {/* Options list */}
      {filteredUsers.length === 0 ? (
        <div className="member-dd-empty">No members found</div>
      ) : (
        <div className="member-dd-list">
          {filteredUsers.map(u => {
            const isSelected = selectedIds.includes(u.id);
            return (
              <div
                key={u.id}
                className={`member-dd-option${isSelected ? ' selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onToggle(u.id);
                  setSearch('');
                  if (!multiSelect) {
                    setIsOpen(false);
                  }
                }}
              >
                {multiSelect && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className="member-dd-checkbox"
                  />
                )}
                <Avatar user={u} size={26} className="member-dd-avatar" />
                <div className="member-dd-info">
                  <span className="member-dd-name">{u.name}</span>
                  <span className={`member-dd-role ${u.role.toLowerCase()}`}>
                    {UserRoleLabels[u.role] || u.role}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`member-dd-wrapper ${className}`} ref={wrapperRef}>
      <button
        type="button"
        className={`member-dd-trigger${isOpen ? ' open' : ''}${selectedCount > 0 ? ' has-selection' : ''}`}
        onClick={handleToggleOpen}
      >
        <span className="member-dd-trigger-label">{triggerLabel}</span>
        <span className={`member-dd-arrow${isOpen ? ' open' : ''}`}>&#9662;</span>
      </button>
      {dropdown}
    </div>
  );
};
